import { lazy, Suspense, useEffect, useState } from 'react';
import { getScopeRole } from '@/services/current-context';
import {
  createBrowserRouter,
  createRoutesFromElements,
  Navigate,
  Outlet,
  Route,
  RouterProvider,
  useLocation,
} from 'react-router-dom';
import { Provider, useDispatch } from 'react-redux';

import { store } from './store/store';
import DashboardUI from '@/components/ui/app-shell';
import { Spinner } from '@/components/ui/spinner';

import TeamMembers from './components/manage-team/team-members/TeamMembers';
import CallBack from './components/callback/CallBack';
import Loader from './components/loader/Loader';
import PrivateRoute from '@/components/ui/private-route';
import { UnscopedConsole } from '@/components/ui/unscoped-console';
import {
  ConsoleRootRedirect,
  LegacyConsoleRedirect,
} from '@/components/ui/legacy-console-redirect';
import NoOrganizations from '@/components/ui/no-organizations';
import { useScopeReconciliation } from './hooks/useScopeReconciliation';
import RegistrationPage from './components/registration/RegistrationPage';
import AdditionalDetailsPage from './components/registration/AdditionalDetailsPage';
import PropertyDetailPage from './components/properties/property-detail/PropertyDetailPage';
import AlertListener from '@/components/ui/alert-listener';
import { Toaster } from '@/components/ui/sonner';
import { Properties } from './components/properties/Properties';
import { PropertyListPage } from './components/properties/property-view/PropertyListPage';
import { FixErrorImports } from './components/properties/bulk-import/fix-errors/FixErrorsPage';
import OrganizationSelectionPage from './components/organization-selection/OrganizationSelectionPage';
import { VendorsIntegration } from './components/pms/VendorsIntegrations';
import IntegrationsLanding from './components/pms/landing/IntegrationsLanding';
import VendorSettingsPage from './components/pms/vendor-settings/VendorSettingsPage';
import { Orders } from './components/orders/Orders';
import { BulkImportPage } from './components/properties/bulk-import/BulkImportPage';
import OrderConfirmationPage from './components/orders/order-confirmation/OrderConfirmationPage';
import CartPage from './components/orders/cart/CartPage';
import BulkImportOrderPage from './components/orders/bulk-import-order/BulkImportOrderPage';
import PaymentPage from './components/orders/payment/PaymentPage';
import PropertyAssetManagementPage from './components/property-assets-management/PropertyAssetManagementPage';
import {
  INTERNAL_CONSOLE_PREFIX,
  LEGACY_CONSOLE_PREFIX,
  ROUTES,
  ROUTE_PATTERNS,
} from './config/routes';
import GalleryLoading from './components/public-gallery/GalleryLoading';
import ContactPage from './components/help-support/ContactPage';
import FaqPage from './components/help-support/FaqPage';
import Unauthorized from '@/components/ui/unauthorized';
import NotFoundPage from '@/components/ui/not-found';
import {
  canCreateOnRoute,
  getPermissionForAction,
  getPermissionForRoute,
  type CreateGuardRoute,
} from './util/permissions';
import { isInternalAdmin, isInternalRole } from './util/roles';
import {
  flushAccessDenialDiagToLogRocket,
  recordAccessDenialDiag,
} from './util/access-denial-log';
import { updateOrgData } from './services/org-data';

import { startTelemetry } from './services/telemetry';
import { setInterceptor } from './services/axios-interceptor';
import { bootstrapAuth } from './services/auth-token';
import useUnifiedSSE from './hooks/useUnifiedSSE';
import { useNavigationPort } from './hooks/useNavigationPort';
import { useSelectedEntityId } from './hooks/useEntityScope';
import { entityRenamed, entityTreeLoaded } from './slice/entityScopeSlice';

// The fallback is eager (tiny) so the gallery chunk stays lazy.
const PublicGalleryPage = lazy(
  () => import('./components/public-gallery/PublicGalleryPage'),
);
const AccountsConsolePage = lazy(
  () => import('./components/accounts-console/AccountsConsolePage'),
);
const AccountDetailPage = lazy(
  () => import('./components/accounts-console/account-detail/AccountDetailPage'),
);
const AdminPromoCodesPage = lazy(
  () => import('./components/admin-promo-codes/AdminPromoCodesPage'),
);
// Named-export lazy: the admin shell (sidebar graph + global-orders view
// metadata) has no business in the customer bundle.
const AdminShell = lazy(() =>
  import('./components/accounts-console/AdminShell').then((m) => ({ default: m.AdminShell })),
);
const GlobalOrdersPage = lazy(
  () => import('./components/global-orders/GlobalOrdersPage'),
);
const CompanyListPage = lazy(
  () => import('./components/company-list/CompanyListPage'),
);

// Redirects the old Buildium OAuth callback URL (pre-March-24 refactor) to
// the current route, preserving the ?token= query param so the OAuth flow
// completes.
const BuildiumRedirect = () => {
  const location = useLocation();
  return (
    <Navigate
      to={`/home/integrations/vendor/buildium${location.search}`}
      replace
    />
  );
};

const ProtectedRoute = ({
  isAllowed,
  children,
}: {
  // Lazy form lets the check evaluate at ProtectedRoute mount (i.e. after
  // the active route activates), so localStorage values populated by an
  // in-flight callback flow are visible. Eager bools still work for
  // checks that don't depend on async state.
  isAllowed: boolean | (() => boolean);
  children: React.ReactNode;
}) => {
  const allowed = typeof isAllowed === 'function' ? isAllowed() : isAllowed;
  if (!allowed) {
    // TEMP diagnostic (remove after root-cause confirmed): snapshot auth state
    // synchronously here so it survives to /error. Intentional in-render write
    // — it must run before <Navigate> triggers the redirect.
    recordAccessDenialDiag('lastAccessDenial', 'access-denied-redirect', {
      source: 'ProtectedRoute',
    });
    return <Navigate to="/error" replace />;
  }
  return children;
};

/**
 * Guard for create/import/checkout surfaces nested under parents that only
 * check GET — without it they are reachable by direct URL for any role that
 * can view the list. canCreateOnRoute keeps that unchanged for every existing
 * role and closes it only for asset-only internal roles (DV-2751). Every new
 * create surface under /home/properties or /home/orders must mount through
 * this guard.
 */
const CreateGuard = ({
  route,
  children,
}: {
  route: CreateGuardRoute;
  children: React.ReactNode;
}) => (
  <ProtectedRoute isAllowed={() => canCreateOnRoute(route)}>
    {children}
  </ProtectedRoute>
);

// Register the axios interceptor synchronously at module load so it's in
// place before any component renders and fires API calls from mount-time
// effects.
setInterceptor();

const getAllPermissionsForRoute = (
  route: string,
): { canPut: boolean; canGet: boolean; canPost: boolean; canDelete: boolean } => ({
  canPut: getPermissionForAction(route, 'PUT'),
  canGet: getPermissionForAction(route, 'GET'),
  canPost: getPermissionForAction(route, 'POST'),
  canDelete: getPermissionForAction(route, 'DELETE'),
});

// Permission reads must happen at render time, not at module load. The
// route tree below is evaluated once at module init; the wrappers below
// re-read localStorage on every match so values reflect the current
// session (post-callback / post-org-switch).
const PropertyListRoute = ({ isProperty }: { isProperty: boolean }) => (
  <PropertyListPage
    isProperty={isProperty}
    canIntegrate={getPermissionForRoute('Integration')}
    {...getAllPermissionsForRoute(isProperty ? 'Properties' : 'Order')}
    canCreate={canCreateOnRoute(isProperty ? 'Properties' : 'Order')}
  />
);

const TeamMembersRoute = () => (
  <TeamMembers {...getAllPermissionsForRoute('External user')} />
);

const BootstrapLoading = () => (
  <div className="flex h-screen w-screen items-center justify-center">
    <Spinner size="lg" />
  </div>
);

/**
 * Layout-route root. Runs `bootstrapAuth` once at mount, holds the spinner
 * until it resolves, and renders the outcome: 'render' mounts the
 * authenticated shell, 'no-organizations' (DV-3175) renders the dead-end
 * screen, and 'redirecting' keeps the spinner up indefinitely so the
 * browser navigation has time to take over the tab.
 *
 * Children only mount once the auth state is known to be usable — this is
 * the reason useUnifiedSSE and RTK Query subscribers live in
 * AuthenticatedShell rather than at the AppLayout level: they would
 * otherwise fire with whatever token happened to be in localStorage,
 * including expired ones, before the bootstrap completes.
 */
const AppLayout = () => {
  const [authState, setAuthState] = useState<'pending' | 'ready' | 'no-organizations'>(
    'pending',
  );

  useEffect(() => {
    bootstrapAuth(window.location.pathname).then((outcome) => {
      if (outcome === 'render') setAuthState('ready');
      else if (outcome === 'no-organizations') setAuthState('no-organizations');
      // 'redirecting': stay on the spinner. bootstrapAuth has already
      // started a full-page navigation (either window.location.assign('/')
      // or endSession's redirect, or the entity-scope step's own
      // navigation); the React tree will unmount when it completes.
    });
  }, []);

  // DV-3175: authenticated, fully registered, member of no organization.
  // Rendered rather than navigated, because routing to '/' re-enters the
  // same bootstrap.
  if (authState === 'no-organizations') return <NoOrganizations />;
  if (authState === 'pending') return <BootstrapLoading />;
  return <AuthenticatedShell />;
};

/**
 * Mounted only after `bootstrapAuth` resolves. Owns the SSE listener,
 * cross-tab logout handler, and LogRocket init.
 */
const AuthenticatedShell = () => {
  // The router a completed entity switch lands through (DV-3013 §3.5). Wired
  // here rather than in AppLayout: AppLayout runs `bootstrapAuth` before this
  // subtree exists, and that call site must keep taking landOn's full-page
  // branch — which is what its 'redirecting' outcome promises.
  useNavigationPort();

  // Cross-tab scope reconciliation (DV-3006 / spec §3.6). Mounted HERE, beside
  // the navigation port and the cross-tab logout arm, for the same reason they
  // are: this subtree exists only once `bootstrapAuth` has resolved, so a
  // reconcile can never race the hydration it compares against.
  useScopeReconciliation();
  const dispatch = useDispatch();

  // Subscribed, not read once at mount: this is the entityUUID useUnifiedSSE
  // subscribes with, so a cached read would keep the stream on the previous
  // account after a reload-free switch (DV-3013 §2.1 class A).
  const selectedEntityId = useSelectedEntityId();
  const selectedEntity = selectedEntityId ?? '';

  const handleEntityNameChange = (newName: string) => {
    // No setTimeout, no refreshTokenOnce, no reload (spec §3.7 / BC-4). The
    // 6-second wait existed to make the refreshed TOKEN's CurrentContext claim
    // carry the new name before the document reloaded — a guess that was
    // either too short (the refreshed token carried the old name) or pure
    // delay. `currentContext` is display-only now (D3) and the header reads
    // the name from the slice, so nothing waits on the claim; the next routine
    // refresh picks it up whenever it happens.
    dispatch(entityRenamed(newName));
    // The cached tree still carries the old label. Clearing it is what makes
    // useEntityTreeNodes re-fetch, which is how the new name reaches the header
    // dropdown and the property list's entity filter.
    dispatch(entityTreeLoaded(null));
    updateOrgData(selectedEntityId, newName);
  };

  useUnifiedSSE(selectedEntity, {
    onEntityNameChange: handleEntityNameChange,
  });

  useEffect(() => {
    // Cross-tab logout: when another tab clears the token, redirect this
    // tab too so the user doesn't end up with one stale authenticated
    // session.
    const handleInvalidToken = () => {
      if (localStorage.getItem('accessToken') === null) {
        window.location.assign('/');
      }
    };
    window.addEventListener('storage', handleInvalidToken);

    // Session replay, on the QA and prod deploys only (services/telemetry.ts).
    startTelemetry();
    // Then ship any access-denial snapshot captured before init (e.g. by a
    // guard redirect on the previous document, before this reload); a no-op
    // where nothing records. See util/access-denial-log.ts.
    flushAccessDenialDiagToLogRocket();

    return () => {
      window.removeEventListener('storage', handleInvalidToken);
    };
  }, []);

  return (
    <>
      <Outlet />
    </>
  );
};

/**
 * The whole route tree, exported so a router-level test can mount it in a
 * memory router (App.test.tsx pins that /gallery/:token runs neither
 * bootstrapAuth nor AuthenticatedShell). The app itself only uses `router`.
 */
export const appRoutes = createRoutesFromElements(
    <>
      {/* Public PhotoPlan gallery (share link): a sibling of AppLayout, not a
          child. Inside the layout it would run bootstrapAuth (a stale stored
          token would log the visitor out and send them to Keycloak) and mount
          AuthenticatedShell, whose cross-tab storage listener redirects to /
          whenever accessToken is absent — which it always is for an anonymous
          visitor, so any same-origin localStorage write (a second gallery
          tab, LogRocket) bounced the page to login. Out here nothing but the
          page itself runs; it fetches an unauthenticated endpoint. */}
      <Route
        path={ROUTE_PATTERNS.PUBLIC_GALLERY}
        element={
          <Suspense fallback={<GalleryLoading />}>
            <PublicGalleryPage />
          </Suspense>
        }
      />
      <Route element={<AppLayout />}>
      <Route path="/" element={<Loader />} />
      <Route path="/callback" element={<CallBack />} />
      <Route path="/error" element={<Unauthorized />} />
      <Route
        path="/register"
        element={
          <PrivateRoute>
            <RegistrationPage />
          </PrivateRoute>
        }
      />
      <Route
        path="/additional-details"
        element={
          <PrivateRoute>
            <AdditionalDetailsPage />
          </PrivateRoute>
        }
      />
      {/* The bare console root has no page: it sends each role to its landing.
          A trimmed URL then works, and so does a bare /account via its twin. */}
      <Route
        path={INTERNAL_CONSOLE_PREFIX}
        element={
          <PrivateRoute>
            <ConsoleRootRedirect />
          </PrivateRoute>
        }
      />
      <Route
        path={ROUTES.ACCOUNTS}
        element={
          <PrivateRoute>
            <ProtectedRoute
              isAllowed={() => isInternalAdmin(getScopeRole())}
            >
              <Suspense fallback={<div>loading please wait...</div>}>
                <AccountsConsolePage />
              </Suspense>
            </ProtectedRoute>
          </PrivateRoute>
        }
      />
      {/* DV-3364: an account's detail page. Internal Admin only, like the list
          (D-13), but `entity: 'any'` (route-guards.ts, strictly below
          /internal/accounts), so a copied link opens in a tab that has an
          account; UnscopedConsole clears it on entry, as on the asset
          workspace below. The <main> is overflow-hidden because the page
          scrolls its own content column, and the sidebar starts collapsed,
          unsaved. */}
      <Route
        path={ROUTE_PATTERNS.ACCOUNT_DETAIL}
        element={
          <PrivateRoute>
            <ProtectedRoute
              isAllowed={() => isInternalAdmin(getScopeRole())}
            >
              <Suspense fallback={<div>loading please wait...</div>}>
                <AdminShell sidebarStartsCollapsed>
                  <main className="relative flex min-w-0 flex-1 flex-col overflow-hidden bg-background">
                    <UnscopedConsole>
                      <AccountDetailPage />
                    </UnscopedConsole>
                  </main>
                </AdminShell>
              </Suspense>
            </ProtectedRoute>
          </PrivateRoute>
        }
      />
      <Route
        path={ROUTES.PROMO_CODES}
        element={
          <PrivateRoute>
            {/* DV-2848 closed this hole: the Admin Console section (Promo
                Codes / Account Management) in adminSideMenu.index.tsx now
                carries requiredRole: 'internal-admin', so it no longer
                renders for any role that merely reaches an admin page. */}
            <ProtectedRoute
              isAllowed={() => isInternalAdmin(getScopeRole())}
            >
              <Suspense fallback={<div>loading please wait...</div>}>
                <AdminPromoCodesPage />
              </Suspense>
            </ProtectedRoute>
          </PrivateRoute>
        }
      />
      <Route
        path={ROUTES.GLOBAL_ORDERS}
        element={
          <PrivateRoute>
            {/* DV-2848: both internal roles — the backend endpoints enforce
                the same pair (INTERNAL_SEARCH_ROLES). */}
            <ProtectedRoute isAllowed={() => isInternalRole(getScopeRole())}>
              <Suspense fallback={<div>loading please wait...</div>}>
                <AdminShell>
                  <GlobalOrdersPage />
                </AdminShell>
              </Suspense>
            </ProtectedRoute>
          </PrivateRoute>
        }
      />
      {/* DV-3520: the console moved from /account to /internal. Old links and
          bookmarks land on their new address, keeping the query string and
          hash; replace, so Back skips the old one. Outside PrivateRoute: the
          destination's guard decides. */}
      <Route
        path={`${LEGACY_CONSOLE_PREFIX}/*`}
        element={<LegacyConsoleRedirect />}
      />
      {/* DV-2849: the internal asset workspace, opened from Global Orders.
          Siblings of the list route, NOT nested — the two stay independent
          elements. Same role gate + shell as the list. The <main> wrapper is
          route-local on purpose: PropertyAssetManagementPage is built around
          AppShell's `relative overflow-auto` scroll column (sticky rows,
          selection bar, drop overlay all resolve against it) and AdminShell
          renders children into a bare flex row. AdminShell itself is not
          changed — the other three /internal pages own their own scrolling.
          UnscopedConsole wraps the page rather than the shell: the shell is
          what should stay on screen while the account is un-chosen, and the
          requests that must not leave scoped all come from the page. */}
      {[ROUTE_PATTERNS.GLOBAL_ORDER_ASSETS, ROUTE_PATTERNS.GLOBAL_ORDER_ASSETS_PHOTOSET].map(
        (pattern) => (
          <Route
            key={pattern}
            path={pattern}
            element={
              <PrivateRoute>
                <ProtectedRoute isAllowed={() => isInternalRole(getScopeRole())}>
                  <Suspense fallback={<div>loading please wait...</div>}>
                    <AdminShell>
                      <main className="relative min-w-0 flex-1 overflow-auto bg-background">
                        <UnscopedConsole>
                          <PropertyAssetManagementPage entry="global-orders" />
                        </UnscopedConsole>
                      </main>
                    </AdminShell>
                  </Suspense>
                </ProtectedRoute>
              </PrivateRoute>
            }
          />
        ),
      )}
      <Route
        path="/select-organization"
        element={
          <PrivateRoute>
            <OrganizationSelectionPage />
          </PrivateRoute>
        }
      />
      <Route
        path="/orders"
        element={
          <PrivateRoute>
            <DashboardUI />
          </PrivateRoute>
        }
      >
        <Route path="new/single" element={<Navigate to="/home/orders" replace />} />
      </Route>
      {/*
        /home/* is always declared so a deep-link visit while
        unauthenticated reaches PrivateRoute, which stores the
        intended URL and redirects through login + org selection.
      */}
      <Route
        path="/home"
        element={
          <PrivateRoute>
            <DashboardUI />
          </PrivateRoute>
        }
      >
        <Route path="help-and-support/faq" element={<FaqPage />} />
        <Route
          path="help-and-support/contact"
          element={<ContactPage />}
        />

        <Route
          path="integrations"
          element={
            <ProtectedRoute
              isAllowed={() => getPermissionForRoute('Integration')}
            >
              <VendorsIntegration />
            </ProtectedRoute>
          }
        >
          <Route index element={<IntegrationsLanding />} />
          <Route path="vendor/:id" element={<VendorSettingsPage />} />
        </Route>

        <Route
          path="properties"
          element={
            <ProtectedRoute
              isAllowed={() => getPermissionForRoute('Properties')}
            >
              <Properties />
            </ProtectedRoute>
          }
        >
          <Route
            path="new/single"
            element={<Navigate to="/home/properties" replace />}
          />
          <Route
            path="new"
            element={<Navigate to="/home/properties" replace />}
          />
          <Route index element={<PropertyListRoute isProperty={true} />} />
          <Route path=":id" element={<PropertyDetailPage />} />
          <Route
            path="new/import"
            element={
              <CreateGuard route="Properties">
                <BulkImportPage isProperty={true} />
              </CreateGuard>
            }
          />
          <Route
            path="new/fix-error"
            element={
              <CreateGuard route="Properties">
                <FixErrorImports />
              </CreateGuard>
            }
          />
          <Route
            path=":propertyUUID/assets"
            element={<PropertyAssetManagementPage />}
          />
          <Route
            path=":propertyUUID/assets/:photosetUUID"
            element={<PropertyAssetManagementPage />}
          />
        </Route>

        <Route
          path="orders"
          element={
            <ProtectedRoute
              isAllowed={() => getPermissionForRoute('Order')}
            >
              <Orders />
            </ProtectedRoute>
          }
        >
          <Route path="new" element={<Navigate to="/home/orders" replace />} />
          <Route path="new/single" element={<Navigate to="/home/orders" replace />} />
          {/* The cart and payment steps are order creation, so they mount
              through CreateGuard like the import routes. */}
          <Route
            path="new/fix-error"
            element={
              <CreateGuard route="Order">
                <FixErrorImports />
              </CreateGuard>
            }
          />
          <Route
            path="new/import"
            element={
              <CreateGuard route="Order">
                <BulkImportOrderPage />
              </CreateGuard>
            }
          />
          <Route
            path="cart"
            element={
              <CreateGuard route="Order">
                <CartPage />
              </CreateGuard>
            }
          />
          <Route
            path="cart/payment"
            element={
              <CreateGuard route="Order">
                <PaymentPage />
              </CreateGuard>
            }
          />
          <Route path="confirmation" element={<OrderConfirmationPage />} />
          <Route index element={<PropertyListRoute isProperty={false} />} />
        </Route>

        <Route
          path="company-list"
          element={
            <ProtectedRoute
              isAllowed={() => getScopeRole() === 'INTERNAL_ADMIN'}
            >
              <Suspense fallback={<div>loading please wait...</div>}>
                <CompanyListPage />
              </Suspense>
            </ProtectedRoute>
          }
        />

        <Route
          path="team-members"
          element={
            <ProtectedRoute
              isAllowed={() => getPermissionForRoute('External user')}
            >
              <TeamMembersRoute />
            </ProtectedRoute>
          }
        />

        {/* Backward-compat redirect: Buildium OAuth callback used the old
            URL structure before the March 24 routing refactor. Preserves
            ?token= so the OAuth flow completes. */}
        <Route
          path="integration/list/vendor/buildium"
          element={<BuildiumRedirect />}
        />
      </Route>
      <Route path="*" element={<NotFoundPage />} />
      </Route>
    </>,
);

const router = createBrowserRouter(appRoutes);

const App = () => (
  <Provider store={store}>
    <AlertListener />
    <Toaster
      position="bottom-right"
      offset={{ bottom: '40px' }}
      closeButton
      duration={6000}
    />
    <RouterProvider router={router} />
  </Provider>
);

export default App;
